import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { NavBar } from '../components/NavBar';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { API_BASE } from '../lib/apiBase';
import type { Home, Task } from '@home-guard/shared';

interface RecommendedContractor {
  id: string;
  business_name: string;
  primary_trade: string;
  review_score: number | null;
  review_count: number;
  response_badge: string | null;
  tier: string;
  is_preferred: number;
}

const VALID_TRADES = [
  'hvac', 'plumbing', 'electrical', 'roofing', 'general',
  'pest_control', 'landscaping', 'painting', 'appliance_repair',
  'flooring', 'chimney', 'garage_door_technician', 'waterproofing_contractor',
] as const;
type Trade = (typeof VALID_TRADES)[number];

const URGENCY_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#9ca3af',
};

const URGENCY_BG: Record<string, string> = {
  critical: '#fef2f2',
  high: '#fff7ed',
  medium: '#fffbeb',
  low: '#f9fafb',
};

function scoreLabel(score: number): string {
  if (score >= 90) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Fair';
  return 'Needs attention';
}

function HealthScoreRing({ score }: { score: number }) {
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width="136" height="136" viewBox="0 0 136 136">
        <circle cx="68" cy="68" r={r} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="10" />
        <circle
          cx="68" cy="68" r={r} fill="none"
          stroke="#fff" strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 68 68)"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
        <text x="68" y="63" textAnchor="middle" fontSize="34" fontWeight="700" fill="#fff">
          {score}
        </text>
        <text x="68" y="82" textAnchor="middle" fontSize="12" fill="rgba(255,255,255,0.75)">
          Health Score
        </text>
      </svg>
      <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 500 }}>
        {scoreLabel(score)}
      </span>
    </div>
  );
}

function formatDueDate(dateStr: string): string {
  const due = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((due.getTime() - now.setHours(0, 0, 0, 0)) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays <= 7) return `Due in ${diffDays} days`;
  return `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

const VERIFY_BANNER_DISMISSED_KEY = 'verify_banner_dismissed';

function EmailVerifyBanner({ userId }: { userId: string }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(`${VERIFY_BANNER_DISMISSED_KEY}:${userId}`); } catch { return false; }
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');

  function dismiss() {
    try { localStorage.setItem(`${VERIFY_BANNER_DISMISSED_KEY}:${userId}`, '1'); } catch { /* private browsing */ }
    setDismissed(true);
  }

  async function resend() {
    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: 'POST',
        credentials: 'include',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (res.ok) {
        setSent(true);
      } else {
        const body = await res.json() as { error?: { message: string } };
        setSendError(body.error?.message ?? 'Could not resend. Try again later.');
      }
    } catch {
      setSendError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  }

  if (dismissed) return null;

  return (
    <div style={{
      background: '#fffbeb',
      borderBottom: '1px solid #fde68a',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 18 }}>📧</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#92400e' }}>
          Please verify your email address
        </p>
        {sent ? (
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#15803d' }}>
            Verification email sent — check your inbox.
          </p>
        ) : (
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#78350f' }}>
            Check your inbox for a verification link.{' '}
            <button
              onClick={resend}
              disabled={sending}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: '#9333EA', fontWeight: 600, fontSize: 12,
                cursor: sending ? 'not-allowed' : 'pointer',
                textDecoration: 'underline',
              }}
            >
              {sending ? 'Sending…' : 'Resend email'}
            </button>
            {sendError && (
              <span style={{ color: '#dc2626', marginLeft: 8, fontSize: 12 }}>{sendError}</span>
            )}
          </p>
        )}
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: 'none', border: 'none', padding: '4px 8px',
          color: '#9ca3af', cursor: 'pointer', fontSize: 16, lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [emergencyTrade, setEmergencyTrade] = useState<Trade>('plumbing');
  const [emergencyDesc, setEmergencyDesc] = useState('');
  const [emergencySubmitting, setEmergencySubmitting] = useState(false);
  const [emergencyError, setEmergencyError] = useState<string | null>(null);
  const [emergencySuccess, setEmergencySuccess] = useState(false);

  const { data: homesData, isLoading: homesLoading } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });

  const homes = homesData?.data ?? [];
  const primaryHome = homes.find((h) => h.is_primary) ?? homes[0];

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', primaryHome?.id, 'pending'],
    queryFn: () => api.get<{ data: Task[] }>(`/homes/${primaryHome!.id}/tasks?filter=pending`),
    enabled: !!primaryHome,
  });

  const { data: recommendedData } = useQuery({
    queryKey: ['recommended', primaryHome?.id],
    queryFn: () => api.get<{ data: RecommendedContractor[] }>(`/homes/${primaryHome!.id}/recommended-contractors`),
    enabled: !!primaryHome,
    staleTime: 5 * 60_000,
  });

  const pendingTasks = tasksData?.data ?? [];
  const urgentTasks = pendingTasks.filter((t) => t.urgency === 'critical' || t.urgency === 'high');
  const overdueTasks = pendingTasks.filter((t) => t.due_date < new Date().toISOString().slice(0, 10));
  const recommendedContractors = recommendedData?.data ?? [];

  const isLoading = homesLoading || (!!primaryHome && tasksLoading);

  async function submitEmergency() {
    setEmergencySubmitting(true);
    setEmergencyError(null);
    try {
      await api.post<unknown>('/emergency/request', {
        trade: emergencyTrade,
        description: emergencyDesc,
        payment_intent_id: 'pi_emergency_placeholder',
      });
      setEmergencySuccess(true);
      setTimeout(() => { setEmergencyOpen(false); setEmergencySuccess(false); setEmergencyDesc(''); }, 3000);
    } catch {
      setEmergencyError('Network error. Please call a contractor directly.');
    } finally {
      setEmergencySubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 32, height: 32,
          border: '3px solid #1D4ED8',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!primaryHome) {
    navigate('/app/home/setup', { replace: true });
    return null;
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 80 }}>
      {user && !user.email_verified && <EmailVerifyBanner userId={user.id} />}
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1D4ED8 0%, #1e40af 100%)',
        padding: '48px 24px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
      }}>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, margin: 0 }}>Good to see you,</p>
            <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: '2px 0 0' }}>
              {user?.name.split(' ')[0]}
            </h1>
          </div>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 16,
          }}>
            {user?.name.charAt(0).toUpperCase()}
          </div>
        </div>

        <HealthScoreRing score={primaryHome.health_score} />
      </div>

      <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Home card */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: '#eff6ff', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            🏠
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {primaryHome.address_line1}
            </p>
            <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: 13 }}>
              {primaryHome.city}, {primaryHome.state} {primaryHome.zip_code}
              {primaryHome.year_built ? ` · Built ${primaryHome.year_built}` : ''}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <StatCard
            value={pendingTasks.length}
            label="Pending tasks"
            color="#1D4ED8"
            bg="#eff6ff"
            onClick={() => navigate('/app/tasks')}
          />
          <StatCard
            value={overdueTasks.length}
            label="Overdue"
            color={overdueTasks.length > 0 ? '#ef4444' : '#22c55e'}
            bg={overdueTasks.length > 0 ? '#fef2f2' : '#f0fdf4'}
            onClick={() => navigate('/app/tasks?filter=overdue')}
          />
        </div>

        {/* Urgent tasks */}
        {urgentTasks.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
                Needs attention
              </h2>
              <button
                onClick={() => navigate('/app/tasks')}
                style={{ background: 'none', border: 'none', color: '#1D4ED8', fontSize: 14, fontWeight: 500, padding: 0, cursor: 'pointer' }}
              >
                See all
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {urgentTasks.slice(0, 4).map((task) => (
                <DashTaskCard key={task.id} task={task} />
              ))}
            </div>
          </div>
        )}

        {pendingTasks.length === 0 && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: 16, padding: '24px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
            <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>All caught up!</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#15803d' }}>
              No pending maintenance tasks.
            </p>
          </div>
        )}

        {/* Emergency button — Part 5 */}
        <button
          onClick={() => setEmergencyOpen(true)}
          style={{
            width: '100%', background: '#fef2f2', border: '1.5px solid #fca5a5',
            borderRadius: 16, padding: '16px', display: 'flex', alignItems: 'center', gap: 12,
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span style={{ fontSize: 24 }}>🚨</span>
          <div>
            <p style={{ margin: 0, fontWeight: 700, color: '#dc2626', fontSize: 15 }}>Emergency home repair?</p>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#ef4444' }}>Alert local contractors now — someone responds in minutes</p>
          </div>
        </button>

        {/* Recommended Pros — Part 9 */}
        {recommendedContractors.length > 0 && (
          <div>
            <h2 style={{ margin: '4px 0 12px', fontSize: 16, fontWeight: 700, color: '#111827' }}>
              Recommended for your home
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recommendedContractors.map(pro => (
                <div
                  key={pro.id}
                  style={{
                    background: '#fff', borderRadius: 14, padding: '14px 16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    display: 'flex', alignItems: 'center', gap: 12,
                    border: pro.is_preferred ? '1.5px solid #6366f1' : '1.5px solid #f3f4f6',
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20,
                  }}>
                    🔧
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pro.business_name}
                      </p>
                      {pro.is_preferred === 1 && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', borderRadius: 6, padding: '1px 6px' }}>
                          PREFERRED
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                      {pro.primary_trade.replace(/_/g, ' ')}
                      {pro.review_score ? ` · ⭐ ${pro.review_score.toFixed(1)} (${pro.review_count})` : ''}
                      {pro.response_badge === 'lightning' ? ' · ⚡ Fast' : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/contractor/${pro.id}`)}
                    style={{
                      background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 10,
                      padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Emergency modal */}
        {emergencyOpen && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}>
            <div style={{
              background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px',
              width: '100%', maxWidth: 480,
            }}>
              {emergencySuccess ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 48 }}>✅</div>
                  <p style={{ fontWeight: 700, color: '#111827', fontSize: 18, margin: '12px 0 4px' }}>Contractors alerted!</p>
                  <p style={{ color: '#6b7280', fontSize: 14 }}>Local pros have been notified. You'll hear back shortly.</p>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>🚨 Emergency request</h2>
                    <button onClick={() => setEmergencyOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af' }}>×</button>
                  </div>
                  <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
                    A $5 routing fee is charged to send your request to all available contractors in your area.
                  </p>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Trade needed</label>
                    <select
                      value={emergencyTrade}
                      onChange={e => setEmergencyTrade(e.target.value as Trade)}
                      style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 10, padding: '10px 12px', fontSize: 14, background: '#fff' }}
                    >
                      {VALID_TRADES.map(t => (
                        <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Describe the problem</label>
                    <textarea
                      value={emergencyDesc}
                      onChange={e => setEmergencyDesc(e.target.value)}
                      placeholder="e.g. Burst pipe under kitchen sink, water everywhere"
                      maxLength={500}
                      rows={3}
                      style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 10, padding: '10px 12px', fontSize: 14, resize: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  {emergencyError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{emergencyError}</p>}
                  <button
                    onClick={submitEmergency}
                    disabled={emergencySubmitting || emergencyDesc.trim().length < 10}
                    style={{
                      width: '100%', background: '#dc2626', color: '#fff', border: 'none',
                      borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
                      opacity: (emergencySubmitting || emergencyDesc.trim().length < 10) ? 0.5 : 1,
                    }}
                  >
                    {emergencySubmitting ? 'Alerting contractors…' : 'Alert local contractors ($5)'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Quick links */}
        <div>
          <h2 style={{ margin: '4px 0 12px', fontSize: 16, fontWeight: 700, color: '#111827' }}>
            Quick access
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {([
              { icon: '🛡️', label: 'Safety',      to: '/app/safety'          },
              { icon: '🏠', label: 'Roof',         to: '/roof'            },
              { icon: '🗑️', label: 'Trash days',  to: '/trash-schedule'  },
              { icon: '📅', label: 'Calendar',     to: '/app/calendar'        },
              { icon: '📋', label: 'History',      to: '/app/history'         },
              { icon: '🔧', label: 'Appliances',   to: '/appliances'      },
            ] as const).map(({ icon, label, to }) => (
              <button
                key={to}
                onClick={() => navigate(to)}
                style={{
                  background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12,
                  padding: '14px 12px', display: 'flex', alignItems: 'center', gap: 10,
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 20 }}>{icon}</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <NavBar />
    </div>
  );
}

function StatCard({
  value, label, color, bg, onClick,
}: {
  value: number;
  label: string;
  color: string;
  bg: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 16, padding: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: 'none', cursor: 'pointer', textAlign: 'left',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 10,
      }}>
        <span style={{ fontSize: 20, fontWeight: 700, color }}>{value}</span>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</p>
    </button>
  );
}

function DashTaskCard({ task }: { task: Task }) {
  const color = URGENCY_COLOR[task.urgency] ?? '#9ca3af';
  const bg = URGENCY_BG[task.urgency] ?? '#f9fafb';

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '14px 16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      borderLeft: `4px solid ${color}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 14, lineHeight: 1.4 }}>
          {task.title}
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
          {task.category}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
        <span style={{
          background: bg, color, fontSize: 11, fontWeight: 600,
          padding: '2px 8px', borderRadius: 20, textTransform: 'capitalize',
        }}>
          {task.urgency}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          {formatDueDate(task.due_date)}
        </span>
      </div>
    </div>
  );
}
